import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Popup from "../components/PopUP";
import { Link, useLocation } from "react-router-dom";
import api from "../Api";
import GameBreadcrumb from "../components/GameBreadcrumb.js";
import TableFooter from "../components/TableFooter";
import {IMAGE_URL} from "../utils/constants";

const DesignHtml = () => {
  const TOKEN_PAGE_LIMIT = 20;
  const [popupMessage, setPopupMessage] = useState("Loading page content...");
  const [levels, setLevel] = useState([]);
  const [endPoint, setEndPoint] = useState("");
  const [tokenLogs, setTokenLogs] = useState([]);
  const [tokenSearchQuery, setTokenSearchQuery] = useState("");
  const [tokenFromDate, setTokenFromDate] = useState("");
  const [tokenToDate, setTokenToDate] = useState("");
  const [tokenPagination, setTokenPagination] = useState({
    count: 0,
    total_pages: 0,
    next: null,
    previous: null,
  });
  const [tokenUsage, setTokenUsage] = useState({
    openAi: 0,
    claudeAi: 0,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  const { pathname } = useLocation();

  useEffect(() => {
    setCurrentPage(1);
  }, [tokenFromDate, tokenToDate]);

  useEffect(() => {
    const fetchData = async () => {

      let endpoint = null;

      if (pathname.includes("quiz-game")) {
        endpoint = "/quiz/";
      } else if (pathname.includes("one-word-game")) {
        endpoint = "/one-word/";
      } else if (pathname.includes("calculation-game")) {
        endpoint = "/calculation/";
      }

      setEndPoint(pathname);
      if (!endpoint) return;

      try {
        const response = await api.get(endpoint);
        setLevel(response.data.data);
      } catch (error) {
        console.error("API Error:", error.message);
      }

      if (endpoint) {
        try {
          const params = {
            page: currentPage,
            limit: TOKEN_PAGE_LIMIT,
            from: tokenFromDate || "",
            to: tokenToDate || "",
          };
          // if (tokenSearchQuery) params.search = tokenSearchQuery;

          const queryString = Object.entries(params)
            .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
            .join('&');
          const tokenResponse = await api.get(
            `${endpoint}admin/token-usage?${queryString}`
          );
          const paginationSource =
            tokenResponse?.data?.pagination || tokenResponse?.data || {};


          setTokenLogs(tokenResponse?.data?.results || []);
          setTokenPagination({
            count: paginationSource?.count || 0,
            total_pages: paginationSource?.total_pages || 0,
            next: paginationSource?.next || null,
            previous: paginationSource?.previous || null,
          });

          const usageSource =
            tokenResponse?.data?.usage ||
            {};
          const openAi =
            Number(
              usageSource?.open_ai ??
              0
            ) || 0;
          const claudeAi =
            Number(
              usageSource?.claude_ai ??
              0
            ) || 0;
          const totalFromApi =
            Number(
              usageSource?.total ??
              0
            ) || 0;
          setTokenUsage({
            openAi,
            claudeAi,
            total: totalFromApi || openAi + claudeAi,
          });
        } catch (error) {
          console.error("Token usage API Error:", error.message);
          setTokenLogs([]);
          setTokenPagination({
            count: 0,
            total_pages: 0,
            next: null,
            previous: null,
          });
          setTokenUsage({
            openAi: 0,
            claudeAi: 0,
            total: 0,
          });
        }
      } else {
        setTokenLogs([]);
        setTokenPagination({
          count: 0,
          total_pages: 0,
          next: null,
          previous: null,
        });
        setTokenUsage({
          openAi: 0,
          claudeAi: 0,
          total: 0,
        });
      }
    };

    fetchData();
  }, [pathname, currentPage, tokenFromDate, tokenToDate]);

  const getLevelLabel = (num) => {
    switch (num) {
      case 1:
        return "Foundation";
      case 2:
        return "Intermediate";
      case 3:
        return "Advance";
      case 4:
        return "Expert";
      case 5:
        return "Master";
      default:
        return "Unknown Level";
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const filteredAndSortedTokenLogs = [...tokenLogs]
    .filter((log) => {
      const search = tokenSearchQuery.trim().toLowerCase();
      if (!search) return true;

      return (
        String(log.model || "")
          .toLowerCase()
          .includes(search) ||
        String(log.input_tokens || "").includes(search) ||
        String(log.output_tokens || "").includes(search) ||
        String(log.total_tokens || "").includes(search) ||
        String(log.role || "")
          .toLowerCase()
          .includes(search) ||
        String(log?.game || "")
          .toLowerCase()
          .includes(search)
      );
    })
    .filter((log) => {
      if (!tokenFromDate && !tokenToDate) return true;

      const rawDate = log?.created_at;
      if (!rawDate) return false;

      const logDate = new Date(rawDate);
      if (Number.isNaN(logDate.getTime())) return false;

      const normalizedLogDate = logDate.toISOString().split("T")[0];
      if (tokenFromDate && normalizedLogDate < tokenFromDate) return false;
      if (tokenToDate && normalizedLogDate > tokenToDate) return false;
      return true;
    })
    .sort((a, b) => {
      const key = sortConfig.key;
      const direction = sortConfig.direction === "asc" ? 1 : -1;
      const aValue = key === "game" ? a?.game : a[key];
      const bValue = key === "game" ? b?.game : b[key];

      if (key === "created_at") {
        return (
          (new Date(aValue || 0).getTime() - new Date(bValue || 0).getTime()) *
          direction
        );
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return String(aValue || "").localeCompare(String(bValue || "")) * direction;
    });

  return (
    <>
      <Popup message={popupMessage} onClose={() => setPopupMessage("")} />
      <Sidebar />
      <div className="main-wrapper">
        <Header />
        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <GameBreadcrumb quizId="" />

                        {/* <h1 className="mb-2 mt-3"></h1> */}
                        {/* <p className="text-white mb-1">
                          This is a description of the Quiz game, detailing its
                          purpose and content for users.
                        </p>
                        <div className="hstack gap-2 gap-sm-4">
                          <span className="text-white">Total Levels: 5</span>
                          <span className="text-white">
                            Total Questions: 120
                          </span>
                        </div> */}
                      </div>
                    </div>
                  </div>

                  <div className="common-table-wrapper mt-4">
                    <div className="common-table-filter-wrapper">
                      <div className="common-left-blk">
                        <div className="common_sub_heading_wrapper mb-0">
                          <h2>Games</h2>
                        </div>
                      </div>
                    </div>
                    <div className="common_content_blk">
                      <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-4 g-4">
                        {levels.map((level) => (
                          <div className="col" key={level.id || level.game_number}>
                            <Link
                              to={`${endPoint}/${level.game_number}`}
                              onClick={() => {
                                sessionStorage.setItem("levelName", level.name);
                                sessionStorage.setItem(
                                  "levelNumber",
                                  level.game_number
                                );
                              }}
                              className="quiz_card_wrapper"
                            >
                              <div className="vstack align-items-center gap-3">
                                <div className="level_icon">
                                  <img
                                    src={`${IMAGE_URL}avio-placeholder.svg`}
                                    alt="avioflAl"
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <h3 className="mb-1 text-center">
                                    {level.name}
                                  </h3>
                                  <p className="mb-0">
                                    <span> Level {level.game_number} - </span>{" "}
                                    {getLevelLabel(level.game_number)}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="common-table-filter-wrapper">
                      <div className="common-left-blk">
                      </div>
                    </div>
                    <div className="common_content_blk">
                      <div className="box_car-card">
                        <div className="box_detail_blk">
                          <div className="common-table-wrapp-4">
                            <div className="common-table-filter-wrapper d-flex justify-content-between align-items-start flex-wrap">
                              <div className="common-left-blk me-3" style={{ minWidth: "240px", flexGrow: 1 }}>
                                <div className="mb-0">
                                  <h4>Set Generation logs</h4>
                                  <br />
                                  <h4> Usage</h4>
                                  <p className="box_info_title mb-1">Total OpenAI tokens: {tokenUsage.openAi}</p>
                                  <p className="box_info_title mb-1">Total ClaudeAI tokens: {tokenUsage.claudeAi}</p>
                                  <p className="box_info_title mb-0">Total tokens: {tokenUsage.total}</p>
                                </div>
                              </div>
                              <div className="common-right-blk d-flex flex-wrap gap-2 align-items-end justify-content-end" style={{ minWidth: "320px" }}>
                                <div className="form-group mb-0" style={{ minWidth: "150px" }}>
                                  <label className="form-label small text-muted">From</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    name="from_date"
                                    value={tokenFromDate}
                                    onChange={(e) => {
                                      const newFromDate = e.target.value;
                                      if (tokenToDate && newFromDate > tokenToDate) {
                                        setTokenToDate(newFromDate); // Optionally adjust To date
                                      }
                                      setTokenFromDate(newFromDate);
                                    }}
                                    max={tokenToDate ? tokenToDate : new Date().toISOString().split(/[T ]/)[0]}
                                  />
                                </div>

                                <div className="form-group mb-0" style={{ minWidth: "150px" }}>
                                  <label className="form-label small text-muted">To</label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    name="to_date"
                                    value={tokenToDate}
                                    onChange={(e) => {
                                      const newToDate = e.target.value;
                                      if (tokenFromDate && newToDate < tokenFromDate) {
                                        setTokenFromDate(newToDate); // Optionally adjust From date
                                      }
                                      setTokenToDate(newToDate);
                                    }}
                                    min={tokenFromDate ? tokenFromDate : ""}
                                    max={new Date().toISOString().split(/[T ]/)[0]}
                                  />
                                </div>

                                {/* <div className="common-search-blk mb-0" style={{ minWidth: "220px" }}>
                                    <input
                                      type="search"
                                      className="form-control"
                                      placeholder="Search logs"
                                      value={tokenSearchQuery}
                                      onChange={(e) => setTokenSearchQuery(e.target.value)}
                                    />
                                  </div> */}
                              </div>
                            </div>
                            <div className="table-blk table-responsive game-level">
                              <table className="table table-hover">
                                <thead>
                                  <tr>
                                    <th>SNo</th>
                                    <th>Game Name</th>
                                    <th>Modal Name</th>
                                    <th>Input</th>
                                    <th>Output</th>
                                    <th>Total</th>
                                    <th>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredAndSortedTokenLogs.length > 0 ? (
                                    filteredAndSortedTokenLogs.map((log, index) => (
                                      <tr key={log.id || index}>
                                        <td className="text-capitalize">
                                          {(currentPage - 1) * TOKEN_PAGE_LIMIT + index + 1}
                                        </td>
                                        <td>{log?.game || "-"} {log?.level && <>({log.level})</>}</td>
                                        <td>{log?.model || "-"}</td>
                                        <td>{log.input_tokens}</td>
                                        <td>{log.output_tokens}</td>
                                        <td>{log.total_tokens}</td>
                                        <td>
                                          {log.created_at
                                            ? new Date(log.created_at).toLocaleString()
                                            : "-"}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="6" className="text-center">No tokens usages found</td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <TableFooter
                              pagination={tokenPagination}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default DesignHtml;
